import type { HTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';

import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

import { cn } from '../lib/utils';

/**
 * Figma Design System Tag
 * https://www.figma.com/design/XAyZZhRbJ3DewYR0ITFfed/Whif?node-id=20928-34580
 *
 * 10 카테고리(Type) × 다중 sub-variant. Figma `get_design_context` 22 symbol 실측 기반.
 * 아이콘은 leftIcon / rightIcon 슬롯으로 주입.
 *
 * **Whif 디자인 토큰 매핑 주의**: Figma 라벨명과 Tailwind 토큰명이 한 단계씩 어긋남.
 *   Figma "Label/Sm" 12px → Tailwind `label-xs-*`
 *   Figma "Label/Md" 14px → Tailwind `label-sm-*`
 *   Figma "Label/Lg" 16px → Tailwind `label-md-*`
 *
 * **폰트 weight 이중 보장**: fontSize 토큰에 fontWeight가 포함돼 있지만,
 * Tailwind JIT/safelist 누락 등으로 weight가 fallback될 수 있어 `font-semibold` /
 * `font-medium` / `font-extrabold` 명시 클래스를 함께 적용함 (QA1·5·7 대응).
 *
 * variant별 스펙 (대부분 h-28 + px-8 + radius-8):
 *   - 14px SemiBold: License / Hashtag(Md) / Character (수/공/Relation/Unsafe) /
 *     Status / Payment Discount/Popular/Cheap
 *   - 12px SemiBold: Author Grade / Amount / Hashtag(Sm)
 *   - 14px Medium:   Nickname / Engagement
 *   - 16px SemiBold: Payment Extra Discount
 *   - 12px Medium:   Beta
 *
 * 특수 layout:
 *   - Engagement / Character Unsafe: bg/padding 없는 인라인 텍스트
 *   - Payment Discount/Extra Discount: pill (radius-full)
 *   - License: 비대칭 radius (tl-6 / tr-12 / br-6 / bl-0)
 *   - Beta: outline border-1 white, padding px-10 py-6, pill
 *   - Hashtag Sm: size='sm' compound로 h-auto + 12px + padding 축소 + radius-6
 */

const tagVariants = cva(
  `
    inline-flex items-center justify-center whitespace-nowrap leading-[1.2]
    transition-colors
    [&_svg]:pointer-events-none [&_svg]:shrink-0
  `,
  {
    defaultVariants: {
      size: 'md',
      variant: 'hashtag',
    },
    variants: {
      size: {
        md: '',
        sm: '',
      },
      variant: {
        license: `
          h-7 px-global-spacing-8 gap-global-spacing-4
          bg-bg-brand-primary-solid text-text-static-solid
          rounded-tl-[6px] rounded-tr-global-radius-12 rounded-br-[6px]
          text-label-sm-sb font-semibold
        `,

        hashtag: `
          h-7 px-global-spacing-8 gap-global-spacing-2
          bg-bg-neutral-weak text-text-neutral-weak
          rounded-global-radius-8
          text-label-sm-sb font-semibold
        `,

        // Hashtag(Matched) — 검색에서 "이 태그가 걸려서 이 결과가 나왔다" 를 표시한다.
        // 판·글자는 hashtag 와 같고 테두리만 더한다 — 보통 해시태그와 나란히 놓였을 때
        // 배경이 달라 보이지 않게 (2026-10-06 디자인 확인).
        'hashtag-matched': `
          h-7 px-global-spacing-8 gap-global-spacing-2
          bg-bg-neutral-weak text-text-neutral-weak
          border border-line-stroke-brand-primary-solid
          rounded-global-radius-8
          text-label-sm-sb font-semibold
        `,

        // Character — Position (수/공 동일 색상)
        'character-main-su': `
          h-7 px-global-spacing-8
          bg-bg-brand-primary-subtle text-text-brand-primary-subtle
          rounded-global-radius-8
          text-label-sm-sb font-semibold
        `,
        'character-main-gong': `
          h-7 px-global-spacing-8
          bg-bg-brand-primary-subtle text-text-brand-primary-subtle
          rounded-global-radius-8
          text-label-sm-sb font-semibold
        `,
        'character-relation': `
          h-7 px-global-spacing-8 gap-global-spacing-4
          bg-bg-brand-primary-weak text-text-brand-primary-solid
          rounded-global-radius-8
          text-label-sm-sb font-semibold
        `,
        // bg/padding 없음 — 아이콘 + 텍스트만
        'character-unsafe': `
          h-7 gap-global-spacing-4
          bg-transparent text-text-brand-secondary-subtle
          text-label-sm-sb font-semibold
        `,

        // Author Grade — 12px SemiBold, 반투명 배경
        // gap 은 형제 등급 변형(rookie·prime·hyper)과 같다 — 빠져 있어 호출부가
        // `className='gap-1'` 로 매번 덧붙이고 있었다.
        'author-default': `
          h-7 px-global-spacing-8 gap-global-spacing-4
          bg-bg-layer-elevated-subtle text-text-neutral-subtle
          rounded-global-radius-8
          text-label-xs-sb font-semibold
        `,
        // 4차 QA8·9·10(스텔라) — 등급 전용 토큰. 종전엔 success/warning/
        // brand-secondary 를 빌려 써서 세 등급이 상태색과 겹쳤다.
        'author-rookie': `
          h-7 px-global-spacing-8 gap-global-spacing-4
          bg-bg-unclassifiable-rookie text-text-unclassifiable-rookie
          rounded-global-radius-8
          text-label-xs-sb font-semibold
        `,
        'author-prime': `
          h-7 px-global-spacing-8 gap-global-spacing-4
          bg-bg-unclassifiable-prime text-text-unclassifiable-prime
          rounded-global-radius-8
          text-label-xs-sb font-semibold
        `,
        'author-hyper': `
          h-7 px-global-spacing-8 gap-global-spacing-4
          bg-bg-unclassifiable-hyper text-text-unclassifiable-hyper
          rounded-global-radius-8
          text-label-xs-sb font-semibold
        `,

        // Nickname (Default/TitleAuthor/Official 공용) — 14px **Medium**
        nickname: `
          h-7 px-global-spacing-8 gap-global-spacing-4
          bg-bg-neutral-subtle text-text-neutral-solid
          rounded-global-radius-8
          text-label-sm-m font-medium
        `,

        // Amount — 12px SemiBold + 반투명. 카드 이미지 위 배지라 양 테마 모두 어두운 반투명 바탕 +
        // 흰 글자다(2026-10-08 QA4). 종전 neutral-subtle/70 + neutral-solid 는 라이트에서 옅은 회색 위
        // 어두운 글자로 뒤집혀 디자인 시스템과 달랐다. 면(이미지 아님) 위에 쓰는 곳은 className 으로 덮는다.
        amount: `
          h-7 px-global-spacing-8 gap-global-spacing-4
          bg-bg-unclassifiable-card-tag-status-info text-text-static-solid
          rounded-global-radius-8
          text-label-xs-sb font-semibold
        `,

        // Engagement (Like/View/Chat) — bg/padding 없음, 14px Medium
        engagement: `
          h-7 gap-global-spacing-4
          bg-transparent text-text-neutral-solid
          text-label-sm-m font-medium
        `,

        // Status — rejected 는 흰 텍스트, pending/private 은 적응 텍스트.
        // pending/private 의 판(bg-neutral-subtle)은 테마 따라 뒤집히는데
        // (다크 #2a2a34 / 라이트 #e7e7e8) 흰 글씨가 고정이라 라이트에서 1.20:1 로
        // 사라져 있었다. 판을 solid 로 올리는 처방(2026-09-16 디자이너 결정 B안)은
        // brand 에만 성립한다 — bg-neutral-* 중 양 테마에서 흰 글씨가 3:1 을 넘는
        // 토큰은 0종이라, 이 둘만 글씨를 적응 토큰으로 돌린다. 다크 외형은 유지된다
        // (#ffffff → #f3f3f4, 대비 14.20 → 13.1).
        'status-pending': `
          h-7 px-global-spacing-8
          bg-bg-neutral-subtle text-text-neutral-solid
          rounded-global-radius-8
          text-label-sm-sb font-semibold
        `,
        'status-rejected': `
          h-7 px-global-spacing-8
          bg-bg-error-solid text-text-static-solid
          rounded-global-radius-8
          text-label-sm-sb font-semibold
        `,
        // 썸네일 위 상태 배지 (Figma 내 작품 검수 12596:30089 · 46×22) — 카드 위 반투명 토큰 + 흰 글씨.
        // status-pending/rejected 는 면 위(리스트 행)용이라 불투명이다.
        'status-overlay-info': `
          h-auto px-global-spacing-6 py-global-spacing-4
          bg-bg-unclassifiable-card-tag-status-info text-text-static-solid
          rounded-global-radius-6
          text-label-xs-sb font-semibold
        `,
        'status-overlay-destructive': `
          h-auto px-global-spacing-6 py-global-spacing-4
          bg-bg-unclassifiable-card-tag-status-destructive text-text-static-solid
          rounded-global-radius-6
          text-label-xs-sb font-semibold
        `,
        'status-private': `
          h-7 px-global-spacing-8
          bg-bg-neutral-subtle text-text-neutral-solid
          rounded-global-radius-8
          text-label-sm-sb font-semibold
        `,

        // Payment — Discount/ExtraDiscount는 pill, Popular/Cheap은 radius-8
        // 판은 pink-70 이다(아래 Figma 주석과 같은 색). brand-secondary-solid 는
        // pink-50 이라 더 밝아, 같은 글자(pink-10)로 2.55:1 까지 떨어진다.
        'payment-discount': `
          h-7 px-global-spacing-8
          bg-bg-unclassifiable-pink-70 text-text-brand-secondary-inverse
          rounded-full
          text-label-sm-sb font-semibold
        `,
        // Figma 134.496deg gradient (purple-50 8.49% → pink-70 91.51%)
        // 16px SemiBold (label-md-sb)
        // 글자는 pink-10(=brand-secondary-inverse). brand-secondary-solid 로 두면
        // 글자가 그라데이션 끝 색과 같은 계열이라 라이트에서 1.30:1 로 사라진다
        // (2026-10-01 쿠폰 결제 페이지 제보). 끝 색도 위 주석대로 pink-70 이다.
        'payment-extra-discount': `
          h-7 px-global-spacing-8
          bg-gradient-to-br from-bg-brand-primary-solid to-bg-unclassifiable-pink-70 text-text-brand-secondary-inverse
          rounded-full
          text-label-md-sb font-semibold
        `,
        'payment-popular': `
          h-7 px-global-spacing-8
          bg-bg-brand-primary-solid text-text-static-solid
          rounded-global-radius-8
          text-label-sm-sb font-semibold
        `,
        'payment-cheap': `
          h-7 px-global-spacing-8
          bg-bg-brand-secondary-solid text-text-static-solid
          rounded-global-radius-8
          text-label-sm-sb font-semibold
        `,

        // Tip — 안내 배너 앞머리 배지. 12px SemiBold, 높이는 내용에 맡긴다(h-7 아님).
        // 배경은 테마별로 갈린다(라이트 blue-70 / 다크 blue-90) — 배너 배경이 라이트에서
        // 옅은 파랑이라 같은 남색을 쓰면 배지가 묻힌다. 글자는 양쪽 다 blue-10.
        tip: `
          px-global-spacing-6 py-global-spacing-2
          bg-bg-info-solid text-text-info-inverse
          rounded-global-radius-4
          text-label-xs-sb font-semibold
        `,

        // Beta — outline border-1 white, pill, 12px Medium
        beta: `
          px-global-spacing-10 py-global-spacing-6
          bg-transparent text-text-neutral-solid
          border border-line-stroke-neutral-solid
          rounded-full
          text-label-xs-m font-medium
        `,
      },
    },
    // hashtag + size='sm' → 12px SemiBold + 작은 padding + radius-6
    compoundVariants: [
      {
        variant: 'hashtag',
        size: 'sm',
        class: `
          h-auto px-global-spacing-6 py-global-spacing-4
          rounded-global-radius-6
          text-label-xs-sb font-semibold
        `,
      },
      // status + size='sm' → 썸네일 위 작은 상태 배지 (Figma Main UI Revamp · 검수
      // 12596:30089 "tag에 sm 사이즈 추가"). 높이 24 + 12px SemiBold + radius-6.
      {
        variant: ['status-pending', 'status-rejected', 'status-private'],
        size: 'sm',
        class: `
          h-6 px-global-spacing-6
          rounded-global-radius-6
          text-label-xs-sb font-semibold
        `,
      },
      // amount + size='sm' → 카드 위 대화량 배지 (Figma Main UI Revamp · 미션 12572:57345,
      // 랭킹 4위+ 12438:70867). 배경은 카드 위 배지 전용 토큰(#41414a 70%)이다 —
      // 기본 amount 의 neutral-subtle/70 은 상점 카드 등 면 위에서 쓰는 값이라 다르다.
      {
        variant: 'amount',
        size: 'sm',
        class: `
          h-auto px-global-spacing-6 py-global-spacing-4
          rounded-global-radius-6
          bg-bg-unclassifiable-card-tag-status-info text-text-static-solid
        `,
      },
      // nickname + size='sm' → 취향 설정의 인기 태그 칩 (Figma 12986:36462 · 28h · Label/Sm/M 12)
      {
        variant: 'nickname',
        size: 'sm',
        class: 'text-label-xs-m font-medium',
      },
      {
        variant: 'hashtag-matched',
        size: 'sm',
        class: `
          h-auto px-global-spacing-6 py-global-spacing-4
          rounded-global-radius-6
          text-label-xs-sb font-semibold
        `,
      },
    ],
  }
);

export type TagVariant = NonNullable<
  VariantProps<typeof tagVariants>['variant']
>;
export type TagSize = NonNullable<VariantProps<typeof tagVariants>['size']>;

export interface TagProps
  extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof tagVariants> {
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const Tag = forwardRef<HTMLSpanElement, TagProps>(
  (
    { className, variant, size, leftIcon, rightIcon, children, ...props },
    ref
  ) => {
    // Figma Hashtag symbol은 # = Pretendard Medium, 텍스트 = Pretendard SemiBold로
    // 두 weight를 사용함. variant 클래스(font-semibold)를 텍스트에 그대로 유지하고
    // # 만 명시적으로 font-medium으로 override.
    const isHashtag = variant === 'hashtag' || variant === 'hashtag-matched';

    return (
      // dir="auto": #·@ 접두 기호가 콘텐츠 언어를 따라 붙는다 — 라틴 태그/닉네임은
      // ltr 로 판정돼 기호가 왼쪽(#charming·@Farm), 아랍어 태그는 rtl 로 오른쪽에
      // 온다. RTL UI 에서 라틴 토큰의 기호가 반대편으로 밀리는 bidi 문제를 막는다.
      <span
        dir='auto'
        className={cn(tagVariants({ className, size, variant }))}
        ref={ref}
        {...props}
      >
        {isHashtag && <span className='font-medium'>#</span>}
        {leftIcon}
        {children}
        {rightIcon}
      </span>
    );
  }
);
Tag.displayName = 'Tag';

export { Tag, tagVariants };
